import { useState, useEffect } from "react";
import { ExtensionRequestModel } from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";
import TextAreaInput from "../TextAreaInput";
import DateInput from "../DateInput";
import { addDays, format, parseISO } from "date-fns";
import differenceInDays from "date-fns/differenceInDays/index";

import './styles.css';

export interface ExtensionRequestProps {
    responseId: string | undefined;
    responseOwnerId: string;
    inventoryId: string | undefined;
    extensionRequest: ExtensionRequestModel | undefined;
    localizer: IStringLocalizer;
    minDaysForExtension?: number;
    maxDaysForExtension?: number;
    updateExtensionRequest?: (extensionRequest: ExtensionRequestModel) => void;
    isButtonDisabled?: boolean;
    disableCheckBoxes?: (isCheckBoxesDisabled: boolean) => void;
    validationType?: any;
}

const ExtensionRequest: React.FC<ExtensionRequestProps> = ({ responseId, responseOwnerId, inventoryId, extensionRequest, localizer, updateExtensionRequest, minDaysForExtension, maxDaysForExtension, isButtonDisabled, disableCheckBoxes, validationType }) => {
    const modalId = "request-extension-modal";
    const minNoDaysForExtension = minDaysForExtension ?? 7;
    const maxNoDaysForExtension = maxDaysForExtension ?? 91;
    const dateFormat = "yyyy-MM-dd";
    const [extensionDate, setExtensionDate] = useState(extensionRequest?.extensionDate ?? format(addDays(new Date(), minNoDaysForExtension), dateFormat));
    const [updatedDate, setUpdatedDate] = useState(extensionDate);
    const [comment, setComment] = useState(extensionRequest?.comment != undefined ? extensionRequest?.comment : "");
    const [isNoOfExtensionDaysValid, setIsNoOfExtensionDaysValid] = useState(true);
    const [currentExtensionRequest, setCurrentExtensionRequest] = useState(extensionRequest);
    const toxnotServiceClient = new ToxnotServiceClient();
    const [disableAnswerButton, setDisableAnswerButton] = useState(false);

    useEffect(() => {
        setDisableAnswerButton(isButtonDisabled || validationType === 2 || validationType === 3);
    }, [isButtonDisabled, validationType]);

    useEffect(() => {
        isValidExtensionDate(extensionDate);
    }, []);

    const clearData = () => {
        setComment("");
        setExtensionDate(format(addDays(new Date(), minNoDaysForExtension), dateFormat));
    }

    const openModal = () => {
        $("#" + modalId).modal('show');
    }

    const isValidExtensionDate = (date: string) => {
        const extensionDays = differenceInDays(new Date(date), new Date());
        const isDateValid = date !== null && date !== "" && extensionDays >= (minNoDaysForExtension - 1) && extensionDays <= (maxNoDaysForExtension - 1);

        setIsNoOfExtensionDaysValid(isDateValid);
        return isDateValid;
    }
    
    const handleDateChange = (date: string) => {
        const isDateValid = isValidExtensionDate(date);

        if (isDateValid) {
            setUpdatedDate(date);
        }
    }

    if (disableCheckBoxes) {
        disableCheckBoxes(currentExtensionRequest?.inventoryIds?.some(i => i === inventoryId) ?? false);
    }

    async function addOrUpdateRequestExtension(): Promise<void> {
        setExtensionDate(updatedDate);

        if (!isNoOfExtensionDaysValid) {
            return;
        }

        let updatedRequestExtension = currentExtensionRequest ?? {
            extensionRequestId: null,
            responseId: responseId,
            responseOwnerId: responseOwnerId,
            extensionDate: null,
            comment: null,
            inventoryIds: [],
        } as ExtensionRequestModel;

        updatedRequestExtension.comment = comment;
        updatedRequestExtension.extensionDate = updatedDate;

        if (inventoryId && !updatedRequestExtension.inventoryIds.some(i => i === inventoryId)) {
            updatedRequestExtension.inventoryIds.push(inventoryId);
        }

        if (responseId) {
            let result = (await toxnotServiceClient.updateExtensionRequest(responseId, updatedRequestExtension));
            if (result.success) {
                if (disableCheckBoxes) {
                    disableCheckBoxes(true);
                }
                updatedRequestExtension.extensionRequestId = (await toxnotServiceClient.updateExtensionRequest(responseId, updatedRequestExtension)).data;
                setCurrentExtensionRequest(updatedRequestExtension);

                if (updateExtensionRequest) {
                    updateExtensionRequest(updatedRequestExtension);
                }
            } else {
                alert(localizer.localizeString("Something went wrong. Please try again."))              
            }
        }
    }

    async function removeRequestExtension(): Promise<void> {
        const updatedRequestExtension = currentExtensionRequest;

        if (updatedRequestExtension && inventoryId && updatedRequestExtension.inventoryIds?.some(i => i === inventoryId)) {
            updatedRequestExtension.inventoryIds = updatedRequestExtension.inventoryIds.filter(i => i !== inventoryId);
            if (updatedRequestExtension.inventoryIds.length === 0 && updatedRequestExtension.extensionRequestId) {
                const result = await toxnotServiceClient.removeExtensionRequest(updatedRequestExtension.extensionRequestId, responseOwnerId, responseId, inventoryId);
                if (result.success) {
                    clearData();
                    updatedRequestExtension.comment = "";
                    updatedRequestExtension.extensionDate = null;

                    setCurrentExtensionRequest(updatedRequestExtension);

                    if (updateExtensionRequest) {
                        updateExtensionRequest(updatedRequestExtension);
                    }
                }
            } else if (responseId) {
                const result = await toxnotServiceClient.updateExtensionRequest(responseId, updatedRequestExtension, inventoryId);

                if (!result.success) {
                    alert(localizer.localizeString("Something went wrong. Please try again."))
                }

                setCurrentExtensionRequest(updatedRequestExtension);

                if (updateExtensionRequest) {
                    updateExtensionRequest(updatedRequestExtension);
                }
            }
        }
    }

    function getExtensionDateToDisplay(date: string){
        return format(parseISO(date ?? ""), dateFormat)
    }

    function onCancel() {
        setIsNoOfExtensionDaysValid(true);
        setUpdatedDate(extensionDate);
    }

    return (
        <>
            <ToxnotReactModal modalId={modalId} localizedTitle={localizer.localizeString("Do you need more time?")}>
                <ToxnotReactModalBody>
                    {
                        <div className="m-5">
                            {(currentExtensionRequest?.inventoryIds ?? []).length > 0 &&
                                <div className="alert alert-warning text-wrap">
                                    {localizer.localizeString("You've already requested an extension for this survey. The date applies to all the materials with extension requested.")}
                                </div>
                            }
                            <p className="instruction-text">{localizer.localizeString("Please only choose this option if you are missing essential information and will have it by a clear date.")}</p>
                            <div className="information-container">
                                <strong>{localizer.localizeString("We will send you a follow up survey on your selected date.")}</strong>
                            </div>
                            <p>{localizer.localizeString("I will have the answer by this date:")}</p>
                            {!isNoOfExtensionDaysValid &&
                                <div className="alert alert-danger" >
                                    {localizer.localizeString("Number of extension days but be between 7 and 91 days.")}
                                </div>
                            }
                            <div className="m-l-25 m-b-10">
                                <DateInput
                                    value={updatedDate}
                                    min={format(addDays(new Date(), minNoDaysForExtension), dateFormat)}
                                    max={format(addDays(new Date(), maxNoDaysForExtension), dateFormat)}
                                    onValueChange={handleDateChange}
                                    required={true}>
                                </DateInput>
                            </div>

                            <div className="m-l-10">
                                <TextAreaInput value={comment} onValueChange={setComment}></TextAreaInput>
                            </div>
                        
                        </div>
                    }
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer}
                    onCancel={onCancel}>
                    {<button disabled={!isNoOfExtensionDaysValid} type="button" className="btn btn-toxnot-primary" data-dismiss="modal" onClick={addOrUpdateRequestExtension}>{localizer.localizeString("Confirm")}</button>}
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <button type="button" className="btn btn-default m-l-5"
                onClick={openModal} disabled={disableAnswerButton}>{currentExtensionRequest?.inventoryIds?.some(i => i === inventoryId) 
                ? `${localizer.localizeString('Update')} ${getExtensionDateToDisplay(extensionDate)} ${localizer.localizeString('Extension')}`
                 : localizer.localizeString("I Can't Answer")}</button>
            {
                currentExtensionRequest?.inventoryIds?.some(i => i === inventoryId) && <button type="button" className="btn btn-default m-l-5"
                    onClick={removeRequestExtension}>{localizer.localizeString("Remove Extension")}</button>
            }
        </>
    );
};

export default ExtensionRequest;